import { Data, Model, Utils } from "@singularsystems/neo-core";
import { AxiosPromise } from "axios";
import { injectable } from "inversify";
import { AppService, Types } from "../../App/Services/AppService";
import CommandResult from "../../Common/Models/CommandResult";
import SaveManualTransactionCommand from "../../DataInterception/GenerateTransactions/Models/Commands/SaveManualTransactionCommand";
import UpdateTransactionCommand from "../../DataInterception/Transactions/Models/Commands/UpdateTransactionCommand";
import RawGenericTransactionLookup from "../../DataInterception/Transactions/Models/Lookups/RawGenericTransactionLookup";
import TransactionInterceptionSearchCriteria from "../../DataInterception/Transactions/Models/Queries/TransactionInterceptionSearchCriteria";
import TransactionLookup from "../Transactions/Models/Lookups/TransactionLookup";
import TransactionSearchCriteria from "../Transactions/Models/Queries/TransactionSearchCriteria";

export interface ITransactionsApiClient {
    getTransactionInterceptionPagedList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionInterceptionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<RawGenericTransactionLookup>>>;
    getTransactionsPagedList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<TransactionLookup>>>;
    getTransactionsList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<TransactionLookup>>>;
    updateSecurityTransactions(command: Model.PlainObject<UpdateTransactionCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    createManualTransactions(command: Model.PlainObject<SaveManualTransactionCommand>): AxiosPromise<Model.PlainObject<CommandResult>>;
    getSecurityTransactions(): AxiosPromise<Array<Model.PlainObject<TransactionSearchCriteria>>>;
}

@injectable()
export default class TransactionsApiClient extends Data.ApiClientBase implements ITransactionsApiClient {
    constructor(config = AppService.get(Types.Config)) {
        super(`${config.apiPath}/Transaction`)
    }

    public getTransactionInterceptionPagedList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionInterceptionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<RawGenericTransactionLookup>>> {
        if(payload.criteria?.clientAccountNumber.includes('|')) 
        {
            payload.criteria.clientAccountNumber = payload.criteria?.clientAccountNumber.split(" | ")[0];
        }
        return this.axios.get(`${this.apiPath}/GetTransactionInterceptionPagedList?${Utils.getQueryString(payload)}`);
    }

    public getSecurityTransactions(): AxiosPromise<Array<Model.PlainObject<TransactionSearchCriteria>>> {
        return this.axios.get(`${this.apiPath}`);
    }

    public getCashTransactionInterceptionPagedList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionInterceptionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<RawGenericTransactionLookup>>> {
        if(payload.criteria?.clientAccountNumber.includes('|')) 
        {
            payload.criteria.clientAccountNumber = payload.criteria?.clientAccountNumber.split(" | ")[0];
        }
        return this.axios.get(`${this.apiPath}/GetCashTransactionInterceptionPagedList?${Utils.getQueryString(payload)}`);
    }

    public getTransactionsPagedList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<TransactionLookup>>> {
        return this.axios.get(`${this.apiPath}/GetTransactionsPagedList?${Utils.getQueryString(payload)}`);
    }

    public getTransactionsList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<TransactionLookup>>> {
        return this.axios.get(`${this.apiPath}/GetTransactionsList?${Utils.getQueryString(payload)}`);
    }

    public getCashTransactionsList(payload: Model.PartialPlainNonTrackedObject<Data.PageRequest<TransactionSearchCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<TransactionLookup>>> {
        return this.axios.get(`${this.apiPath}/GetCashTransactionsList?${Utils.getQueryString(payload)}`);
    }

    public updateSecurityTransactions(command: Model.PlainObject<UpdateTransactionCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.patch(`${this.apiPath}/UpdateSecurityTransactions`, command);
    }

    public updateCashTransactions(command: Model.PlainObject<UpdateTransactionCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.patch(`${this.apiPath}/UpdateCashTransactions`, command);
    }

    public createManualTransactions(command: Model.PlainObject<SaveManualTransactionCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/CreateManualTransactions`, command);
    }
}